import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "../services/query-keys"
import {
  useRenderProductVisualPreview,
  type ProductVisualRenderInput,
  type ProductVisualRenderedPreview,
} from "../services/use-product-visual-preview"

function cellKey(input: ProductVisualRenderInput): string {
  return `${input.simpleSku}:${input.visualKind}`
}

/** `queued` is waiting for the single render slot; `rendering` holds it. */
export type ProductVisualRenderStatus = "idle" | "queued" | "rendering"

/** Where one cell stands with its own Render action. */
type ProductVisualCellRender = {
  status: ProductVisualRenderStatus
  error: string | null
  /** The session-ephemeral JPEG this cell last produced. Kept while a re-render
   *  is in flight so the image the operator is judging does not blink away. */
  preview: ProductVisualRenderedPreview | undefined
}

const IDLE: ProductVisualCellRender = { status: "idle", error: null, preview: undefined }

/** Per-cell render state for the product page's visuals gallery, over a single
 *  render slot.
 *
 *  **The slot is the point.** Chromium is one Cloud Run service pinned to
 *  `min 0 / max 5 / concurrency 1` (`infra/application-cloudrun.tf`, issue #350),
 *  so the whole fleet has 5 render slots. A gallery draws N simples × 6 kinds,
 *  each with its own Render button, so firing them concurrently would put every
 *  click past those 5 into Cloud Run's queue, to come back as a 429 or to trip
 *  the API's 60s render timeout — each *after* spending a source resolve and a
 *  content write. A batch render can be holding all 5 at the same moment.
 *
 *  This hook keeps **one** slot, not 5, and that is deliberate rather than stale:
 *  a gallery is one person inspecting cells, where a queued second click costs
 *  nothing, while the 5 slots exist to drain a store in one batch. Clicks
 *  therefore **queue** rather than fan out — no click is lost, and this view puts
 *  one request at a time on the worker.
 *
 *  Per-cell state is still keyed by cell and driven from that call's own promise
 *  — the mutation's `isPending` / `variables` / `error` describe only the latest
 *  call and would drop an earlier cell's result and spinner.
 *
 *  The rendered JPEGs live in component state on purpose: the preview POST
 *  persists content but never an image, so these are session-ephemeral and must
 *  not be written into the query cache as if they were the persisted read. The
 *  content it *does* persist is why the gallery read is invalidated on settle. */
export function useProductVisualRender(configSku: string, store: string) {
  const { mutateAsync } = useRenderProductVisualPreview(store)
  const queryClient = useQueryClient()
  const [cells, setCells] = useState<Record<string, ProductVisualCellRender>>({})
  // The queue *is* this chain: appending is enqueueing, and the tail is the only
  // thing holding the slot. A ref, not state — the queue's depth is not
  // something the gallery re-renders on; each cell's own status is.
  const slotRef = useRef<Promise<unknown>>(Promise.resolve())

  const patchCell = useCallback((key: string, change: Partial<ProductVisualCellRender>) => {
    setCells((current) => ({ ...current, [key]: { ...(current[key] ?? IDLE), ...change } }))
  }, [])

  const renderCell = useCallback(
    (input: ProductVisualRenderInput) => {
      const key = cellKey(input)
      patchCell(key, { status: "queued", error: null })
      // Never throws, so the chain always stays resolved: one failed render must
      // not strand every render queued behind it.
      const takeSlot = async () => {
        patchCell(key, { status: "rendering" })
        try {
          const preview = await mutateAsync(input)
          patchCell(key, { status: "idle", error: null, preview })
        } catch (error: unknown) {
          patchCell(key, {
            status: "idle",
            error: error instanceof Error ? error.message : "Render failed.",
          })
        }
        try {
          // The POST persists canonical content, so the gallery's persisted
          // state can have moved.
          await queryClient.invalidateQueries({
            queryKey: queryKeys.productConfigVisuals(configSku, store),
          })
        } catch {
          // A failed refetch leaves the cell's own result on screen; the query's
          // error state owns surfacing it, and the slot must still pass on.
        }
      }
      // Both arms: the predecessor rejecting must not skip this render.
      slotRef.current = slotRef.current.then(takeSlot, takeSlot)
    },
    [configSku, mutateAsync, patchCell, queryClient, store]
  )

  return {
    renderCell,
    stateFor: (input: ProductVisualRenderInput): ProductVisualCellRender =>
      cells[cellKey(input)] ?? IDLE,
  }
}
